'use client';

import { c as _c } from "react/compiler-runtime";
import { deleteUserDimensionMapping, listUserDimensionMappings, upsertUserDimensionMapping } from '@/api/generated/nebuly-admin/nebuly-admin';
import type { UserDimension } from '@/api/generated/model';
import { QUERY_KEY_FETCH_ANALYTICS_CHART_DATA, QUERY_KEY_INTERACTION_AGGREGATES } from '@/constants/query-keys';
import { useProjectsContext } from '@/providers/projects-provider';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { toast } from 'sonner';
import { NEBULY_PAGE_QUERY_OPTIONS } from '@/domains/pages/shared/page-query-options';
const USER_DIMENSION_MAPPINGS_QUERY_KEY = 'userDimensionMappings';
const EMPTY_MAPPED_TAG_KEYS: string[] = [];
export function useUserDimensionMapping(field, enabled) {
  const $ = _c(44);
  const {
    projectId
  } = useProjectsContext();
  const queryClient = useQueryClient();
  let t0;
  let t1;
  if ($[0] !== projectId) {
    t0 = [USER_DIMENSION_MAPPINGS_QUERY_KEY, projectId];
    t1 = t2 => {
      const {
        signal
      } = t2;
      return listUserDimensionMappings({
        project_id: projectId
      }, {
        signal
      });
    };
    $[0] = projectId;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  const t2 = enabled && !!projectId;
  let t3;
  if ($[3] !== t0 || $[4] !== t1 || $[5] !== t2) {
    t3 = {
      queryKey: t0,
      queryFn: t1,
      enabled: t2,
      ...NEBULY_PAGE_QUERY_OPTIONS
    };
    $[3] = t0;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const mappingsQuery = useQuery(t3);
  let t4;
  if ($[7] !== field || $[8] !== mappingsQuery.data?.mappings) {
    t4 = mappingsQuery.data?.mappings.find(mapping => mapping.field === field);
    $[7] = field;
    $[8] = mappingsQuery.data?.mappings;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const currentMapping = t4;
  currentMapping?.source_tag_keys;
  const mappedTagKeys = currentMapping?.source_tag_keys ?? EMPTY_MAPPED_TAG_KEYS;
  const hasMapping = mappedTagKeys.length > 0;
  let t5;
  if ($[10] !== projectId || $[11] !== queryClient) {
    t5 = async () => {
      await Promise.all([queryClient.invalidateQueries({
        queryKey: [USER_DIMENSION_MAPPINGS_QUERY_KEY, projectId]
      }), queryClient.invalidateQueries({
        queryKey: [QUERY_KEY_INTERACTION_AGGREGATES]
      }), queryClient.invalidateQueries({
        queryKey: [QUERY_KEY_FETCH_ANALYTICS_CHART_DATA]
      })]);
      await queryClient.refetchQueries({
        queryKey: [QUERY_KEY_INTERACTION_AGGREGATES],
        type: "active"
      });
    };
    $[10] = projectId;
    $[11] = queryClient;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  const invalidateMappingQueries = t5;
  let t6;
  if ($[13] !== field || $[14] !== projectId) {
    t6 = sourceTagKeys => upsertUserDimensionMapping({
      project_id: projectId,
      field,
      source_tag_keys: sourceTagKeys
    });
    $[13] = field;
    $[14] = projectId;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  if ($[16] !== invalidateMappingQueries) {
    t7 = async () => {
      await invalidateMappingQueries();
      toast.success("Mapping saved. Segment data will refresh shortly.");
    };
    $[16] = invalidateMappingQueries;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  if ($[18] !== t6 || $[19] !== t7) {
    t8 = {
      mutationFn: t6,
      onSuccess: t7,
      onError: _temp
    };
    $[18] = t6;
    $[19] = t7;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  const upsertMutation = useMutation(t8);
  let t9;
  if ($[21] !== field || $[22] !== projectId) {
    t9 = () => deleteUserDimensionMapping({
      project_id: projectId,
      field
    });
    $[21] = field;
    $[22] = projectId;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  let t10;
  if ($[24] !== invalidateMappingQueries) {
    t10 = async () => {
      await invalidateMappingQueries();
      toast.success("Mapping removed. Segment data will refresh shortly.");
    };
    $[24] = invalidateMappingQueries;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  let t11;
  if ($[26] !== t10 || $[27] !== t9) {
    t11 = {
      mutationFn: t9,
      onSuccess: t10,
      onError: _temp2
    };
    $[26] = t10;
    $[27] = t9;
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  const deleteMutation = useMutation(t11);
  let t12;
  if ($[29] !== projectId || $[30] !== upsertMutation) {
    t12 = async sourceTagKey => {
      if (!projectId) {
        return;
      }
      await upsertMutation.mutateAsync(sourceTagKey ? [sourceTagKey] : []);
    };
    $[29] = projectId;
    $[30] = upsertMutation;
    $[31] = t12;
  } else {
    t12 = $[31];
  }
  const saveMapping = t12;
  let t13;
  if ($[32] !== deleteMutation || $[33] !== projectId) {
    t13 = async () => {
      if (!projectId) {
        return;
      }
      await deleteMutation.mutateAsync();
    };
    $[32] = deleteMutation;
    $[33] = projectId;
    $[34] = t13;
  } else {
    t13 = $[34];
  }
  const removeMapping = t13;
  const isMutating = upsertMutation.isPending || deleteMutation.isPending;
  let t14;
  if ($[35] !== deleteMutation.isPending || $[36] !== hasMapping || $[37] !== isMutating || $[38] !== mappedTagKeys || $[39] !== mappingsQuery.isLoading || $[40] !== removeMapping || $[41] !== saveMapping || $[42] !== upsertMutation.isPending) {
    t14 = {
      mappedTagKeys,
      hasMapping,
      isLoading: mappingsQuery.isLoading,
      isSaving: upsertMutation.isPending,
      isRemoving: deleteMutation.isPending,
      isMutating,
      saveMapping,
      removeMapping
    };
    $[35] = deleteMutation.isPending;
    $[36] = hasMapping;
    $[37] = isMutating;
    $[38] = mappedTagKeys;
    $[39] = mappingsQuery.isLoading;
    $[40] = removeMapping;
    $[41] = saveMapping;
    $[42] = upsertMutation.isPending;
    $[43] = t14;
  } else {
    t14 = $[43];
  }
  return t14;
}
function _temp2() {
  toast.error("Failed to remove mapping. Please try again.");
}
function _temp() {
  toast.error("Failed to save mapping. Please try again.");
}